import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { getMerchantData } from '../api'
import { useMode } from '../context/ModeContext'
import { useBusinessContext } from '../context/BusinessContext'
import { snapshotPage, popPageSnapshot, restoreScroll } from '../utils/pageState'
import { TransactionsTable } from '../components/txDisplay'
import type { Transaction } from '../types'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler)

const money = (v: number) => '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const PALETTE = ['#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#f59e0b','#10b981','#06b6d4','#3b82f6','#84cc16']

function merchColor(name: string | null | undefined): string {
  if (!name) return '#94a3b8'
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff
  return PALETTE[h % PALETTE.length]
}

type Period   = 'last12' | 'all' | number
type MonthSel = { year: number; month: number } | null
type MerchTab = 'active' | 'recurring' | 'new'
type TxSortF  = 'date' | 'merchant' | 'details' | 'category' | 'tag' | 'card' | 'charges'
type SortDir  = 'asc' | 'desc'
interface MonthPill { year: number; month: number; label: string }

interface MerchantRow {
  name: string
  totalSpent: number
  transactionCount: number
  avgTransaction: number
  percentage: number
}

const todayGlobal = new Date()

function computeMonthPills(period: Period): MonthPill[] {
  if (period === 'all') return []
  if (period === 'last12') {
    const pills: MonthPill[] = []
    for (let i = 12; i >= 0; i--) {
      const d  = new Date(todayGlobal.getFullYear(), todayGlobal.getMonth() - i, 1)
      const yr = d.getFullYear()
      const mo = d.getMonth() + 1
      pills.push({ year: yr, month: mo, label: MONTH_NAMES[mo - 1] + " '" + String(yr).slice(2) })
    }
    return pills
  }
  const year     = period as number
  const maxMonth = year === todayGlobal.getFullYear() ? todayGlobal.getMonth() + 1 : 12
  return Array.from({ length: maxMonth }, (_, i) => ({ year, month: i + 1, label: MONTH_NAMES[i] }))
}

function buildChartMonths(refYear: number, refMonth: number, offset = 0): string[] {
  const months: string[] = []
  for (let i = 11; i >= 0; i--) {
    const d = new Date(refYear, refMonth - 1 - i + offset, 1)
    months.push(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'))
  }
  return months
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CHART_OPTS: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1e293b', padding: 10, cornerRadius: 10,
      callbacks: { label: (ctx: { raw: unknown }) => money(ctx.raw as number) },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } },
    y: {
      beginAtZero: true, grid: { color: 'rgba(148,163,184,0.15)' }, border: { display: false },
      ticks: { color: '#94a3b8', font: { size: 10 }, callback: (v: unknown) => '$' + ((v as number) >= 1000 ? ((v as number) / 1000).toFixed(0) + 'k' : v) },
    },
  },
}

interface MerchPageState {
  period: Period; selectedMonth: MonthSel
  selectedMerchant: string | null; merchantTab: MerchTab
  selectedCategory: string | null; selectedTag: string | null
  search: string; txSort: TxSortF; txSortDir: SortDir
}

export default function Merchants() {
  const navigate = useNavigate()
  const { mode } = useMode()
  const { selectedBusinessId } = useBusinessContext()
  const isPersonal = mode === 'personal'
  const bizId      = mode === 'business' ? selectedBusinessId : null
  const autoSelectRef = useRef(true)

  const [period,           setPeriod]           = useState<Period>('last12')
  const [selectedMonth,    setSelectedMonth]    = useState<MonthSel>({ year: todayGlobal.getFullYear(), month: todayGlobal.getMonth() + 1 })
  const [selectedMerchant, setSelectedMerchant] = useState<string | null>(null)
  const [merchantTab,      setMerchantTab]      = useState<MerchTab>('active')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedTag,      setSelectedTag]      = useState<string | null>(null)
  const [search,           setSearch]           = useState('')
  const [chartOffset,      setChartOffset]      = useState(0)
  const [txSort,           setTxSort]           = useState<TxSortF>('date')
  const [txSortDir,        setTxSortDir]        = useState<SortDir>('desc')
  const [pendingScroll,    setPendingScroll]    = useState<number | null>(null)

  useEffect(() => {
    const snap = popPageSnapshot<MerchPageState>('merchants')
    if (!snap) return
    const s = snap.state
    setPeriod(s.period); setSelectedMonth(s.selectedMonth)
    setSelectedMerchant(s.selectedMerchant); setMerchantTab(s.merchantTab)
    setSelectedCategory(s.selectedCategory ?? null); setSelectedTag(s.selectedTag ?? null)
    if (s.search) setSearch(s.search)
    setTxSort(s.txSort); setTxSortDir(s.txSortDir)
    setPendingScroll(snap.scroll)
    autoSelectRef.current = false
  }, [])

  const monthPills  = useMemo(() => computeMonthPills(period), [period])
  const isAllPeriod = period === 'all'
  const apiYear     = isAllPeriod ? undefined : period === 'last12' ? selectedMonth?.year : (period as number)
  const apiMonth    = selectedMonth?.month

  const { data: mainData, isLoading, error } = useQuery({
    queryKey: isAllPeriod ? ['merchants', 'all'] : ['merchants', apiYear, apiMonth],
    queryFn:  () => getMerchantData(apiYear, apiMonth, isAllPeriod, isPersonal, bizId),
  })

  // All-time data — used for recurring/new computation and chart range
  const { data: allTimeData } = useQuery({
    queryKey: ['merchants', 'all'],
    queryFn:  () => getMerchantData(undefined, undefined, true, isPersonal, bizId),
    staleTime: 5 * 60_000,
  })

  // Chart year queries (3 years back for full nav range)
  const chartRefYearNum = selectedMonth?.year
    ?? (typeof period === 'number' ? (period as number) : todayGlobal.getFullYear())
  const { data: chartYearData } = useQuery({
    queryKey: ['merchants', chartRefYearNum, undefined],
    queryFn:  () => getMerchantData(chartRefYearNum, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null,
    staleTime: 5 * 60_000,
  })
  const { data: chartPriorYearData } = useQuery({
    queryKey: ['merchants', chartRefYearNum - 1, undefined],
    queryFn:  () => getMerchantData(chartRefYearNum - 1, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null,
    staleTime: 5 * 60_000,
  })
  const { data: chartPrior2YearData } = useQuery({
    queryKey: ['merchants', chartRefYearNum - 2, undefined],
    queryFn:  () => getMerchantData(chartRefYearNum - 2, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null,
    staleTime: 5 * 60_000,
  })

  const allTxns     = (mainData?.transactions    ?? []) as Transaction[]
  const allTimeTxns = (allTimeData?.transactions ?? []) as Transaction[]

  // Chart reference point (end of 12-month window)
  const chartRefYear  = selectedMonth?.year
    ?? (typeof period === 'number' ? (period as number) : todayGlobal.getFullYear())
  const chartRefMonth = selectedMonth?.month
    ?? (typeof period === 'number'
      ? ((period as number) === todayGlobal.getFullYear() ? todayGlobal.getMonth() + 1 : 12)
      : todayGlobal.getMonth() + 1)

  // ── Merchant list computations ──────────────────────────────────────────────

  const activeMerchants = useMemo<MerchantRow[]>(() => {
    const periodTotal = allTxns.reduce((s, t) => s + Math.abs(t.charges ?? 0), 0)
    const map = new Map<string, { spent: number; count: number }>()
    for (const t of allTxns) {
      if (!t.merchantDisplayName) continue
      const ex = map.get(t.merchantDisplayName) ?? { spent: 0, count: 0 }
      map.set(t.merchantDisplayName, { spent: ex.spent + Math.abs(t.charges ?? 0), count: ex.count + 1 })
    }
    return Array.from(map.entries())
      .map(([name, s]) => ({ name, totalSpent: s.spent, transactionCount: s.count, avgTransaction: s.count > 0 ? s.spent / s.count : 0, percentage: periodTotal > 0 ? (s.spent / periodTotal) * 100 : 0 }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
  }, [allTxns])

  const recurringMerchants = useMemo<MerchantRow[]>(() => {
    const monthSet = new Set(buildChartMonths(chartRefYear, chartRefMonth, 0))
    const windowTotal = allTimeTxns.filter(t => t.date && monthSet.has(t.date.slice(0, 7))).reduce((s, t) => s + Math.abs(t.charges ?? 0), 0)
    const map = new Map<string, { spent: number; count: number }>()
    for (const t of allTimeTxns) {
      if (!t.merchantDisplayName || !t.date || !monthSet.has(t.date.slice(0, 7))) continue
      const ex = map.get(t.merchantDisplayName) ?? { spent: 0, count: 0 }
      map.set(t.merchantDisplayName, { spent: ex.spent + Math.abs(t.charges ?? 0), count: ex.count + 1 })
    }
    return Array.from(map.entries())
      .map(([name, s]) => ({ name, totalSpent: s.spent, transactionCount: s.count, avgTransaction: s.count > 0 ? s.spent / s.count : 0, percentage: windowTotal > 0 ? (s.spent / windowTotal) * 100 : 0 }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
  }, [allTimeTxns, chartRefYear, chartRefMonth])

  const newMerchants = useMemo<MerchantRow[]>(() => {
    const cutoffYM = selectedMonth
      ? `${selectedMonth.year}-${String(selectedMonth.month).padStart(2, '0')}`
      : typeof period === 'number'
        ? `${period as number}-01`
        : buildChartMonths(chartRefYear, chartRefMonth, 0)[0]
    const earliestByMerchant = new Map<string, string>()
    for (const t of allTimeTxns) {
      if (!t.merchantDisplayName || !t.date) continue
      const cur = earliestByMerchant.get(t.merchantDisplayName)
      if (!cur || t.date < cur) earliestByMerchant.set(t.merchantDisplayName, t.date)
    }
    const periodTotal = allTxns.reduce((s, t) => s + Math.abs(t.charges ?? 0), 0)
    const map = new Map<string, { spent: number; count: number }>()
    for (const t of allTxns) {
      if (!t.merchantDisplayName) continue
      const ex = map.get(t.merchantDisplayName) ?? { spent: 0, count: 0 }
      map.set(t.merchantDisplayName, { spent: ex.spent + Math.abs(t.charges ?? 0), count: ex.count + 1 })
    }
    return Array.from(map.entries())
      .filter(([name]) => { const e = earliestByMerchant.get(name); return !e || e.slice(0, 7) >= cutoffYM })
      .map(([name, s]) => ({ name, totalSpent: s.spent, transactionCount: s.count, avgTransaction: s.count > 0 ? s.spent / s.count : 0, percentage: periodTotal > 0 ? (s.spent / periodTotal) * 100 : 0 }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
  }, [allTxns, allTimeTxns, selectedMonth, period, chartRefYear, chartRefMonth])

  const displayList = merchantTab === 'active' ? activeMerchants : merchantTab === 'recurring' ? recurringMerchants : newMerchants

  // Search filter — persists across period/month changes
  const filteredList = search.trim()
    ? displayList.filter(m => m.name.toLowerCase().includes(search.trim().toLowerCase()))
    : displayList

  // Auto-select first merchant from filtered list whenever it changes
  useEffect(() => {
    if (!autoSelectRef.current || filteredList.length === 0) return
    setSelectedMerchant(filteredList[0].name)
    setSelectedCategory(null)
    setSelectedTag(null)
    setChartOffset(0)
    autoSelectRef.current = false
  }, [filteredList])

  // ── Chart ──────────────────────────────────────────────────────────────────

  const chartSource = useMemo<Transaction[]>(() => {
    if (selectedMonth != null) {
      return [
        ...((chartYearData?.transactions    ?? []) as Transaction[]),
        ...((chartPriorYearData?.transactions  ?? []) as Transaction[]),
        ...((chartPrior2YearData?.transactions ?? []) as Transaction[]),
      ]
    }
    return allTxns
  }, [selectedMonth, chartYearData, chartPriorYearData, chartPrior2YearData, allTxns])

  const chartData = useMemo(() => {
    if (!selectedMerchant) return null
    let txns = chartSource.filter(t => t.merchantDisplayName === selectedMerchant)
    if (selectedCategory) txns = txns.filter(t =>
      t.category === selectedCategory ||
      t.splitLines?.some(l => l.category === selectedCategory)
    )
    if (selectedTag) txns = txns.filter(t =>
      t.tag?.split(',').map(s => s.trim()).includes(selectedTag) ||
      t.splitLines?.some(l => l.tags?.includes(selectedTag))
    )
    const months = buildChartMonths(chartRefYear, chartRefMonth, chartOffset)
    const map = new Map<string, number>()
    for (const t of txns) {
      if (!t.date) continue
      const ym = t.date.slice(0, 7)
      map.set(ym, (map.get(ym) ?? 0) + Math.abs(t.charges ?? 0))
    }
    const color = merchColor(selectedMerchant)
    return {
      labels: months.map(ym => { const [y, m] = ym.split('-'); return MONTH_NAMES[parseInt(m, 10) - 1] + " '" + String(y).slice(2) }),
      datasets: [{ label: selectedMerchant, data: months.map(ym => map.get(ym) ?? 0), borderColor: color, backgroundColor: color + '22', fill: true, tension: 0.4, pointRadius: 3, pointHoverRadius: 6, borderWidth: 2, pointBackgroundColor: color }],
    }
  }, [chartSource, selectedMerchant, selectedCategory, selectedTag, chartRefYear, chartRefMonth, chartOffset])

  const chartDataRange = useMemo(() => {
    if (!selectedMerchant) return null
    const source = [...allTimeTxns, ...chartSource, ...allTxns]
    const months = source.filter(t => t.merchantDisplayName === selectedMerchant && t.date).map(t => t.date!.slice(0, 7))
    if (months.length === 0) return null
    return { min: months.reduce((a, b) => a < b ? a : b), max: months.reduce((a, b) => a > b ? a : b) }
  }, [allTimeTxns, chartSource, allTxns, selectedMerchant])

  const chartActiveMonths = useMemo(() => {
    if (!selectedMerchant || !chartData) return 0
    return chartData.datasets[0].data.filter((v: number) => v > 0).length
  }, [selectedMerchant, chartData])

  const merchantFirstLast = useMemo(() => {
    if (!selectedMerchant) return null
    const dates = allTimeTxns.filter(t => t.merchantDisplayName === selectedMerchant && t.date).map(t => t.date!)
    if (dates.length === 0) return null
    return { first: dates.reduce((a, b) => a < b ? a : b), last: dates.reduce((a, b) => a > b ? a : b) }
  }, [allTimeTxns, selectedMerchant])

  const merchantCategories = useMemo(() => {
    if (!selectedMerchant) return []
    const txns  = allTxns.filter(t => t.merchantDisplayName === selectedMerchant)
    const total = txns.reduce((s, t) => s + Math.abs(t.charges ?? 0), 0)
    // Use per-line amounts so split transactions contribute each line's amount
    // to the correct category rather than the full transaction charge.
    const spendMap = new Map<string, number>()
    const countMap = new Map<string, Set<string>>()
    for (const t of txns) {
      const lines = (t.isSplit && t.splitLines?.length)
        ? t.splitLines
        : [{ category: t.category, amount: t.charges ?? 0 }]
      for (const l of lines) {
        if (!l.category) continue
        spendMap.set(l.category, (spendMap.get(l.category) ?? 0) + Math.abs(l.amount))
        if (!countMap.has(l.category)) countMap.set(l.category, new Set())
        countMap.get(l.category)!.add(String(t.id))
      }
    }
    return Array.from(spendMap.entries())
      .map(([name, spent]) => ({
        name,
        totalSpent:  spent,
        count:       countMap.get(name)?.size ?? 0,
        percentage:  total > 0 ? (spent / total) * 100 : 0
      }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
  }, [allTxns, selectedMerchant])

  const merchantTags = useMemo(() => {
    if (!selectedMerchant) return []
    const txns  = allTxns.filter(t => t.merchantDisplayName === selectedMerchant)
    const total = txns.reduce((s, t) => s + Math.abs(t.charges ?? 0), 0)
    // Use per-line amounts so split lines contribute their own amount to each tag.
    const spendMap = new Map<string, number>()
    const countMap = new Map<string, Set<string>>()
    for (const t of txns) {
      if (t.isSplit && t.splitLines?.length) {
        for (const l of t.splitLines)
          for (const tag of l.tags ?? []) {
            spendMap.set(tag, (spendMap.get(tag) ?? 0) + Math.abs(l.amount))
            if (!countMap.has(tag)) countMap.set(tag, new Set())
            countMap.get(tag)!.add(String(t.id))
          }
      } else {
        for (const tag of t.tag?.split(',').map(s => s.trim()).filter(Boolean) ?? []) {
          spendMap.set(tag, (spendMap.get(tag) ?? 0) + Math.abs(t.charges ?? 0))
          if (!countMap.has(tag)) countMap.set(tag, new Set())
          countMap.get(tag)!.add(String(t.id))
        }
      }
    }
    return Array.from(spendMap.entries())
      .map(([name, spent]) => ({
        name,
        totalSpent:  spent,
        count:       countMap.get(name)?.size ?? 0,
        percentage:  total > 0 ? (spent / total) * 100 : 0
      }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
  }, [allTxns, selectedMerchant])

  const sortedTxns = useMemo(() => {
    let txns = [...allTxns]
    if (selectedMerchant  != null) txns = txns.filter(t => t.merchantDisplayName === selectedMerchant)
    if (selectedCategory != null) txns = txns.filter(t =>
      t.category === selectedCategory ||
      t.splitLines?.some(l => l.category === selectedCategory)
    )
    if (selectedTag != null) txns = txns.filter(t =>
      t.tag?.split(',').map(s => s.trim()).includes(selectedTag) ||
      t.splitLines?.some(l => l.tags?.includes(selectedTag))
    )
    return txns.sort((a, b) => {
      let av: string | number = '', bv: string | number = ''
      if      (txSort === 'date')     { av = a.date ?? '';     bv = b.date ?? '' }
      else if (txSort === 'merchant') { av = a.merchantDisplayName ?? ''; bv = b.merchantDisplayName ?? '' }
      else if (txSort === 'details')  { av = a.description ?? '';         bv = b.description ?? '' }
      else if (txSort === 'category') { av = a.category ?? ''; bv = b.category ?? '' }
      else if (txSort === 'tag')      { av = a.tag ?? '';       bv = b.tag ?? '' }
      else if (txSort === 'card')     { av = a.account ?? '';  bv = b.account ?? '' }
      else if (txSort === 'charges')  { av = a.charges ?? 0;   bv = b.charges ?? 0 }
      if (typeof av === 'string') { const c = av.localeCompare(bv as string); return txSortDir === 'asc' ? c : -c }
      return txSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [allTxns, selectedMerchant, selectedCategory, selectedTag, txSort, txSortDir])


  const goToEdit = (id: string) => {
    snapshotPage<MerchPageState>('merchants', { period, selectedMonth, selectedMerchant, merchantTab, selectedCategory, selectedTag, search, txSort, txSortDir })
    navigate('/transactions/' + id + '/edit')
  }

  // Period/month change handlers — search persists intentionally
  const changePeriod = (p: Period) => {
    autoSelectRef.current = true; setChartOffset(0)
    setPeriod(p); setSelectedMonth(null); setSelectedMerchant(null)
    setSelectedCategory(null); setSelectedTag(null)
  }
  const changeMonth = (m: MonthSel) => {
    autoSelectRef.current = true; setChartOffset(0)
    setSelectedMonth(m); setSelectedMerchant(null)
    setSelectedCategory(null); setSelectedTag(null)
  }
  const selectMerchant = (name: string) => {
    setChartOffset(0)
    setSelectedMerchant(prev => {
      if (prev === name) return null
      setSelectedCategory(null); setSelectedTag(null)
      return name
    })
  }
  const changeTab = (tab: MerchTab) => {
    setMerchantTab(tab); setSelectedMerchant(null)
    setSelectedCategory(null); setSelectedTag(null)
    setChartOffset(0); autoSelectRef.current = true
  }

  if (isLoading) return <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>
  if (error || !mainData) return <div className="pb-empty"><i className="bi bi-exclamation-triangle me-2" />Failed to load merchants.</div>

  if (pendingScroll != null && !isLoading) { restoreScroll(pendingScroll); setPendingScroll(null) }

  const thisYear  = new Date().getFullYear()
  const earliest  = mainData.earliestYear ?? thisYear
  const minYear   = Math.max(earliest, thisYear - 5)
  const yearRange: number[] = []
  for (let y = thisYear; y >= minYear; y--) yearRange.push(y)
  const periodBtns: { label: string; value: Period }[] = [
    { label: 'Last 12 Mo', value: 'last12' },
    ...yearRange.map(y => ({ label: String(y), value: y as Period })),
    { label: 'All', value: 'all' },
  ]

  const uniqueMerchants = activeMerchants.length
  const totalSpent      = activeMerchants.reduce((s, m) => s + m.totalSpent, 0)

  const detailRow  = selectedMerchant ? (displayList.find(m => m.name === selectedMerchant) ?? activeMerchants.find(m => m.name === selectedMerchant) ?? null) : null
  const detailColor = merchColor(selectedMerchant)

  const chartRefLabel     = MONTH_NAMES[chartRefMonth - 1] + " '" + String(chartRefYear).slice(2)
  const chartWindowMonths = buildChartMonths(chartRefYear, chartRefMonth, chartOffset)
  const canGoBack    = chartDataRange != null && chartWindowMonths[0]  > chartDataRange.min
  const canGoForward = chartDataRange != null && chartWindowMonths[11] < chartDataRange.max

  const hasFilters = selectedMerchant != null || selectedCategory != null || selectedTag != null

  return (
    <div>
      <h1 className="pb-page-title mb-3">Merchants</h1>

      {/* Period selector */}
      <div className="mb-3">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          {periodBtns.map(b => (
            <button key={String(b.value)} className={'pb-chart-sel-btn' + (period === b.value ? ' active' : '')} onClick={() => changePeriod(b.value)}>{b.label}</button>
          ))}
        </div>
      </div>

      {/* Month pills */}
      {monthPills.length > 0 && (
        <div className="pb-month-pills mb-4">
          <button className={'pb-month-pill' + (selectedMonth === null ? ' active' : '')} onClick={() => changeMonth(null)}>All</button>
          {monthPills.map(p => {
            const isActive = selectedMonth?.year === p.year && selectedMonth?.month === p.month
            return <button key={p.year + '-' + p.month} className={'pb-month-pill' + (isActive ? ' active' : '')} onClick={() => changeMonth({ year: p.year, month: p.month })}>{p.label}</button>
          })}
        </div>
      )}

      {/* KPI strip */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-arrow-up-circle me-1" />Total Spent</div><div className="pb-kpi-value" style={{ color: 'var(--pb-danger)' }}>{money(totalSpent)}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-arrow-down-circle me-1" />Total Earned</div><div className="pb-kpi-value" style={{ color: '#16a34a' }}>{money(mainData.totalEarned)}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-shop me-1" />Unique Merchants</div><div className="pb-kpi-value">{uniqueMerchants}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-receipt me-1" />Transactions</div><div className="pb-kpi-value">{mainData.totalTransactions.toLocaleString()}</div></div></div>
      </div>

      {/* Two-column layout */}
      <div className="row g-4 mb-4">

        {/* ── Left: Merchant list ── */}
        <div className="col-lg-6">
          <div className="pb-card" style={{ padding: 0 }}>

            {/* Tabs header */}
            <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span className="pb-kpi-label"><i className="bi bi-shop me-1" />Ranked by Spending</span>
              <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
                <button className={'pb-chart-sel-btn' + (merchantTab === 'active'    ? ' active' : '')} onClick={() => changeTab('active')}>
                  <i className="bi bi-bar-chart me-1" />Active ({activeMerchants.length})
                </button>
                <button className={'pb-chart-sel-btn' + (merchantTab === 'recurring' ? ' active' : '')} onClick={() => changeTab('recurring')}>
                  <i className="bi bi-arrow-repeat me-1" />Recurring ({recurringMerchants.length})
                </button>
                <button className={'pb-chart-sel-btn' + (merchantTab === 'new'       ? ' active' : '')} onClick={() => changeTab('new')}>
                  <i className="bi bi-stars me-1" />New ({newMerchants.length})
                </button>
              </div>
            </div>

            {/* Search — persists across period/month changes */}
            <div style={{ padding: '0.55rem 1rem', borderBottom: '1px solid var(--pb-border)' }}>
              <div style={{ position: 'relative' }}>
                <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--pb-muted)', fontSize: '0.8rem', pointerEvents: 'none' }} />
                <input
                  value={search}
                  onChange={e => { autoSelectRef.current = true; setSearch(e.target.value) }}
                  placeholder="Search merchants..."
                  style={{ width: '100%', paddingLeft: 30, paddingRight: search ? 28 : 10, paddingTop: '0.35rem', paddingBottom: '0.35rem', fontSize: '0.82rem', border: '1px solid var(--pb-border)', borderRadius: 8, background: 'var(--pb-glass-bg)', color: 'var(--pb-text)', outline: 'none' }}
                />
                {search && (
                  <button onClick={() => { autoSelectRef.current = true; setSearch('') }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--pb-muted)', lineHeight: 1 }}>
                    <i className="bi bi-x" style={{ fontSize: '0.9rem' }} />
                  </button>
                )}
              </div>
            </div>

            {/* Merchant rows — max 13 visible, then scroll */}
            {filteredList.length === 0 ? (
              <div className="pb-empty" style={{ padding: '2rem' }}>
                {search
                  ? `No merchants match "${search}".`
                  : merchantTab === 'active'    ? 'No merchant data for this period.'
                  : merchantTab === 'recurring' ? 'No recurring merchants found for this period.'
                  : 'No new merchants found for this period.'}
              </div>
            ) : (
              <div style={{ maxHeight: 'calc(13 * 4.17rem)', overflowY: 'auto' }}>
                {filteredList.map((m, idx) => {
                  const isCredit = m.totalSpent < 0
                  const color    = isCredit ? '#16a34a' : merchColor(m.name)
                  const isActive = selectedMerchant === m.name
                  const amtDisplay = isCredit
                    ? '-' + money(m.totalSpent)   // money() does Math.abs, prefix with minus
                    : money(m.totalSpent)
                  return (
                    <div
                      key={m.name}
                      onClick={() => selectMerchant(m.name)}
                      style={{ display: 'grid', gridTemplateColumns: '2.2rem 1fr auto', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', borderBottom: '1px solid var(--pb-border)', cursor: 'pointer', background: isActive ? color + '18' : undefined, transition: 'background 0.1s' }}
                      onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                      onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = '' }}
                    >
                      {/* Rank or credit icon */}
                      <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: idx < 99 ? '0.72rem' : '0.6rem', color }}>
                        {isCredit ? <i className="bi bi-arrow-down-circle" style={{ fontSize: '0.88rem' }} /> : idx + 1}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: isActive ? color : isCredit ? color : 'var(--pb-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {m.name}
                          {isActive && <i className="bi bi-check-circle-fill ms-1" style={{ color, fontSize: '0.72rem' }} />}
                          {merchantTab === 'new' && <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.35rem', borderRadius: 4, background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>NEW</span>}
                          {isCredit && <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.35rem', borderRadius: 4, background: 'rgba(22,163,74,0.12)', color: '#16a34a' }}>CREDIT</span>}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{m.transactionCount} txns &middot; {money(m.avgTransaction)}/txn</div>
                        <div style={{ marginTop: 4, height: 3, borderRadius: 4, background: 'var(--pb-border)', overflow: 'hidden' }}>
                          <div style={{ width: Math.min(Math.abs(m.percentage), 100) + '%', height: '100%', background: color, borderRadius: 4 }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: isCredit ? '#16a34a' : undefined }}>{amtDisplay}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{Math.abs(m.percentage).toFixed(1)}%</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Detail panel ── */}
        <div className="col-lg-6">
          <div className="pb-card" style={{ minHeight: 200 }}>

            {selectedMerchant == null && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, color: 'var(--pb-muted)' }}>
                <i className="bi bi-hand-index" style={{ fontSize: '2rem', opacity: 0.3, marginBottom: '0.75rem' }} />
                <div style={{ fontSize: '0.88rem' }}>Select a merchant to view details</div>
              </div>
            )}

            {selectedMerchant && detailRow && (
              <div>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  <span style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: detailColor + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem', color: detailColor }}>
                    {selectedMerchant.charAt(0).toUpperCase()}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedMerchant}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--pb-muted)', marginTop: 3 }}>{detailRow.transactionCount} transactions</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.25rem', color: detailRow.totalSpent < 0 ? '#16a34a' : undefined }}>
                      {detailRow.totalSpent < 0 ? '-' + money(detailRow.totalSpent) : money(detailRow.totalSpent)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--pb-muted)' }}>{Math.abs(detailRow.percentage).toFixed(1)}% of {detailRow.totalSpent < 0 ? 'credit' : 'total'}</div>
                  </div>
                </div>

                {/* KPI row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1.25rem' }}>
                  {[
                    { label: 'Avg Transaction', value: money(detailRow.avgTransaction), icon: 'bi-calculator' },
                    { label: 'Active Months',   value: String(chartActiveMonths),        icon: 'bi-calendar-check' },
                    { label: 'First Seen',       value: merchantFirstLast?.first?.slice(0, 7) ?? '—', icon: 'bi-calendar-plus' },
                    { label: 'Last Activity',    value: merchantFirstLast?.last?.slice(0, 7)  ?? '—', icon: 'bi-calendar-event' },
                  ].map(kpi => (
                    <div key={kpi.label} style={{ background: 'var(--pb-glass-bg)', borderRadius: 8, padding: '0.6rem 0.65rem', border: '1px solid var(--pb-border)' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--pb-muted)', marginBottom: 4 }}>
                        <i className={'bi ' + kpi.icon + ' me-1'} />{kpi.label}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{kpi.value}</div>
                    </div>
                  ))}
                </div>

                {/* Spending chart */}
                {chartData && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)' }}>
                        Spending over time — as of {chartRefLabel}
                        {(selectedCategory || selectedTag) && <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6 }}>— filtered</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button onClick={() => setChartOffset(o => o - 6)} disabled={!canGoBack}    style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoBack    ? 'pointer' : 'default', color: canGoBack    ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-left"  style={{ fontSize: '0.85rem' }} /></button>
                        <button onClick={() => setChartOffset(o => o + 6)} disabled={!canGoForward} style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoForward ? 'pointer' : 'default', color: canGoForward ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-right" style={{ fontSize: '0.85rem' }} /></button>
                      </div>
                    </div>
                    <div style={{ height: 150 }}><Line data={chartData} options={CHART_OPTS} /></div>
                  </div>
                )}

                {/* Categories — selectable, filters chart + table */}
                {merchantCategories.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)', marginBottom: 10 }}>Categories</div>
                    {merchantCategories.map(c => {
                      const isCatActive = selectedCategory === c.name
                      const cColor = merchColor(c.name)
                      return (
                        <div
                          key={c.name}
                          onClick={() => { setChartOffset(0); setSelectedCategory(prev => prev === c.name ? null : c.name); setSelectedTag(null) }}
                          style={{ marginBottom: 6, cursor: 'pointer', borderRadius: 6, padding: '0.35rem 0.5rem', background: isCatActive ? cColor + '18' : undefined, border: isCatActive ? '1.5px solid ' + cColor + '66' : '1.5px solid transparent', transition: 'all 0.1s' }}
                          onMouseEnter={e => { if (!isCatActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                          onMouseLeave={e => { if (!isCatActive) (e.currentTarget as HTMLElement).style.background = '' }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: isCatActive ? 700 : 500, color: isCatActive ? 'var(--pb-primary)' : undefined }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: cColor, flexShrink: 0 }} />
                              {c.name}
                              {isCatActive && <i className="bi bi-check-circle-fill" style={{ color: 'var(--pb-primary)', fontSize: '0.68rem' }} />}
                              <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{c.count}&times;</span>
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{c.percentage.toFixed(1)}%</span>
                              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{money(c.totalSpent)}</span>
                            </div>
                          </div>
                          <div style={{ height: 4, borderRadius: 4, background: 'var(--pb-border)', overflow: 'hidden' }}>
                            <div style={{ width: Math.min(c.percentage, 100) + '%', height: '100%', background: cColor, borderRadius: 4 }} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Tags — selectable, filters chart + table */}
                {merchantTags.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)', marginBottom: 10 }}>Tags</div>
                    {merchantTags.map(tag => {
                      const isTagActive = selectedTag === tag.name
                      return (
                        <div
                          key={tag.name}
                          onClick={() => { setChartOffset(0); setSelectedTag(prev => prev === tag.name ? null : tag.name); setSelectedCategory(null) }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.3rem 0.5rem', marginBottom: 2, borderRadius: 6, cursor: 'pointer', background: isTagActive ? 'rgba(99,102,241,0.08)' : undefined, border: isTagActive ? '1.5px solid rgba(99,102,241,0.4)' : '1.5px solid transparent', transition: 'all 0.1s' }}
                          onMouseEnter={e => { if (!isTagActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                          onMouseLeave={e => { if (!isTagActive) (e.currentTarget as HTMLElement).style.background = '' }}
                        >
                          <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: isTagActive ? 700 : 500 }}>
                            <span style={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: 600, padding: '0.1rem 0.45rem', borderRadius: 6, background: isTagActive ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)', marginRight: 4 }}>{tag.name}</span>
                            {isTagActive && <i className="bi bi-check-circle-fill" style={{ color: 'var(--pb-primary)', fontSize: '0.68rem', marginRight: 4 }} />}
                            <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{tag.count}&times;</span>
                          </span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{tag.percentage.toFixed(1)}%</span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{money(tag.totalSpent)}</span>
                        </div>
                      )
                    })}
                  </div>
                )}

                {merchantCategories.length === 0 && merchantTags.length === 0 && (
                  <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', textAlign: 'center', padding: '1rem 0' }}>No category or tag data for this merchant.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transaction table */}
      <TransactionsTable
        items={sortedTxns as Transaction[]}
        showPB
        stateKey="merchants"
        resetKey={`${selectedMerchant ?? ''}|${selectedCategory ?? ''}|${selectedTag ?? ''}`}
        onRowClick={goToEdit}
        headerExtra={hasFilters ? (
          <>
            {selectedMerchant != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-shop" style={{ fontSize: '0.75rem' }} />{selectedMerchant}
                <button onClick={() => { setSelectedMerchant(null); setSelectedCategory(null); setSelectedTag(null) }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
            {selectedCategory != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-tag" style={{ fontSize: '0.75rem' }} />{selectedCategory}
                <button onClick={() => setSelectedCategory(null)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
            {selectedTag != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-bookmark" style={{ fontSize: '0.75rem' }} />{selectedTag}
                <button onClick={() => setSelectedTag(null)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
          </>
        ) : undefined}
      />
    </div>
  )
}
